import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import PageHeading from '../../components/common/PageHeading';
import { getEventReplays } from '../../services/hubEvents.service';
import api from '../../services/api';

/** Format a Hub event's date/time for display, in the member's own timezone. */
const formatEventTime = (event) => {
  const raw = event.event_datetime || event.event_date || event.scheduled_date;
  if (!raw) return null;
  const d = new Date(raw);
  const hasTime = event.event_datetime != null;
  return d.toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    ...(hasTime ? { hour: 'numeric', minute: '2-digit', hour12: true } : {}),
  });
};

export default function Replays() {
  const [replays, setReplays] = useState(null);
  const [openReplayId, setOpenReplayId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getEventReplays()
      .then(setReplays)
      .catch(() => setError('Could not load replays.'));
  }, []);

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <PageHeading title="Replays" subtitle="Each recording stays here for 7 days after the live event." />
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}

        {!replays ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : replays.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
            No replays recorded yet this week — check back soon.
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
            {replays.map(r => (
              <div key={r.ghl_event_id}>
                <button
                  onClick={() => {
                    // Opening a replay counts as watching it (Sales & Marketing, 2026-09-28) — fire-and-forget.
                    if (openReplayId !== r.ghl_event_id) api.post('/members/me/engagement', { type: 'replay_watch', ref: r.title || r.ghl_event_id }).catch(() => {});
                    setOpenReplayId(openReplayId === r.ghl_event_id ? null : r.ghl_event_id);
                  }}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition"
                >
                  {r.thumbnail_url ? (
                    <img
                      src={r.thumbnail_url}
                      alt={r.thumbnail_alt_text || `Replay of ${r.title}`}
                      className="w-24 aspect-video rounded-lg object-contain shrink-0 bg-reset-terracotta/10"
                    />
                  ) : (
                    <span className="text-xl shrink-0" aria-hidden="true">▶️</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 leading-snug truncate">{r.title}</p>
                    {formatEventTime(r) && (
                      <p className="text-xs text-gray-400 mt-0.5">{formatEventTime(r)}</p>
                    )}
                    {r.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{r.description}</p>
                    )}
                  </div>
                  <span className="shrink-0 text-xs text-reset-terracotta font-medium">
                    {openReplayId === r.ghl_event_id ? 'Close' : 'Watch →'}
                  </span>
                </button>
                {openReplayId === r.ghl_event_id && (
                  <div className="px-5 pb-5 space-y-4">
                    <div>
                      {r.additional_replay_urls?.length > 0 && (
                        <p className="text-xs font-medium text-gray-400 mb-1.5">Part 1</p>
                      )}
                      <div className="rounded-2xl overflow-hidden shadow-sm bg-black aspect-video">
                        <iframe
                          src={r.replay_url}
                          title={r.title}
                          className="w-full h-full"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </div>
                    {(r.additional_replay_urls || []).map((url, i) => (
                      <div key={url}>
                        <p className="text-xs font-medium text-gray-400 mb-1.5">Part {i + 2}</p>
                        <div className="rounded-2xl overflow-hidden shadow-sm bg-black aspect-video">
                          <iframe
                            src={url}
                            title={`${r.title} — Part ${i + 2}`}
                            className="w-full h-full"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
